/* eslint-disable react-refresh/only-export-components */
/**
 * EditorStateProvider
 *
 * Contexte dédié pour gérer l'état de l'éditeur de sondages (Business Logic)
 * Extrait de ConversationProvider pour éviter re-renders inutiles
 *
 * Responsabilités :
 * - État éditeur (ouvert/fermé)
 * - Sondage en cours d'édition (Date ou Form)
 * - Dispatch actions vers reducers
 * - Persistence du sondage
 *
 * @see Docs/Architecture-ConversationProvider.md
 */

import React, {
  createContext,
  useContext,
  useState,
  useReducer,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useSafeLocation } from "@core/hooks/useSafeLocation";
import { logError, ErrorFactory } from "@core/lib/error-handling";
import { pollReducer, type PollAction } from "@core/reducers/pollReducer";
import {
  addPoll,
  type Poll,
  type FormQuestionShape as PollFormQuestion,
  type FormQuestionKind,
} from "@core/lib/pollStorage";
import { logger } from "@core/lib/logger";
import { usePolls } from "@core/hooks/usePolls";
// import { useFormPollCreation } from "@core/hooks/useFormPollCreation"; // MIGRÉ vers apps/form-polls/src/hooks/
import type {
  PollSuggestion,
  FormQuestion as PollSuggestionFormQuestion,
} from "@core/types/poll-suggestions";

// Type guard for date questions
function isDateQuestion(q: { type: string }): q is {
  type: "date";
  selectedDates?: string[];
  timeSlotsByDate?: Record<string, any>;
  timeGranularity?: string;
  allowMaybeVotes?: boolean;
  allowAnonymousVotes?: boolean;
} {
  return q.type === "date";
}

// Helper function to map between poll-suggestions FormQuestion and pollStorage FormQuestionShape
function mapToStorageQuestion(
  q: PollSuggestionFormQuestion,
  uid: () => string,
): PollFormQuestion {
  // Create the base question with required fields
  const baseQuestion: PollFormQuestion = {
    id: uid(),
    kind: (q.type === "date" ? "single" : q.type) as FormQuestionKind, // Map 'date' to 'single' for now
    title: q.title || q.text || "Sans titre",
    required: q.required || false,
  };

  // Add type for backward compatibility
  const question = {
    ...baseQuestion,
    type: q.type as FormQuestionKind,
  };

  // Handle different question types
  if (q.type === "single" || q.type === "multiple") {
    return {
      ...question,
      options: (q.options || [])
        .filter(
          (opt): opt is string => typeof opt === "string" && opt.trim() !== "",
        )
        .map((opt) => ({
          id: uid(),
          label: opt.trim(),
        })),
      ...(q.maxChoices && { maxChoices: q.maxChoices }),
    };
  }

  // Handle date question type
  if (isDateQuestion(q)) {
    return {
      ...question,
      selectedDates: q.selectedDates || [],
      timeSlotsByDate: q.timeSlotsByDate || {},
      timeGranularity: q.timeGranularity || "30min",
      allowMaybeVotes: q.allowMaybeVotes || false,
      allowAnonymousVotes: q.allowAnonymousVotes || false,
    } as unknown as PollFormQuestion; // Type assertion needed for date-specific properties
  }

  // Handle other question types
  return {
    ...question,
    ...(q.placeholder && { placeholder: q.placeholder }),
    ...(q.maxLength && { maxLength: q.maxLength }),
    ...(q.validationType && { validationType: q.validationType }),
    ...(q.ratingScale && { ratingScale: q.ratingScale }),
    ...(q.ratingStyle && { ratingStyle: q.ratingStyle }),
    ...(q.ratingMinLabel && { ratingMinLabel: q.ratingMinLabel }),
    ...(q.ratingMaxLabel && { ratingMaxLabel: q.ratingMaxLabel }),
    ...(q.matrixRows && { matrixRows: q.matrixRows }),
    ...(q.matrixColumns && { matrixColumns: q.matrixColumns }),
    ...(q.matrixType && { matrixType: q.matrixType }),
    ...(q.matrixColumnsNumeric !== undefined && {
      matrixColumnsNumeric: q.matrixColumnsNumeric,
    }),
  };
}

export interface EditorStateContextType {
  // État éditeur
  isEditorOpen: boolean;
  currentPoll: Poll | null;

  // Actions éditeur
  openEditor: () => void;
  closeEditor: () => void;
  toggleEditor: () => void;

  // Actions sondage
  dispatchPollAction: (action: PollAction) => void;
  setCurrentPoll: (poll: Poll | null) => void;
  clearCurrentPoll: () => void;

  // Actions combinées
  createPollFromChat: (
    pollData: Partial<Poll> | Poll | PollSuggestion,
  ) => Promise<void>;
}

const EditorStateContext = createContext<EditorStateContextType | undefined>(
  undefined,
);

const STORAGE_KEY = "prototype_current_poll";

interface EditorStateProviderProps {
  children: ReactNode;
}

export function EditorStateProvider({ children }: EditorStateProviderProps) {
  const { t } = useTranslation("common");
  const location = useSafeLocation();
  const { createPoll, updatePoll } = usePolls();
  // const { createFormPoll } = useFormPollCreation(); // MIGRÉ vers apps/form-polls/src/hooks/
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [currentPoll, dispatchPoll] = useReducer(pollReducer, null);
  const justClearedRef = useRef(false); // 🔧 FIX: Flag pour éviter restauration après nettoyage
  // Id retourné par addPoll() lors de l'auto-persist. Si présent, "Créer ce sondage"
  // réutilise ce poll au lieu d'en créer un nouveau (évite roundtrip + SLUG_COLLISION).
  const persistedPollIdRef = useRef<string | null>(null);

  // Hook pour accéder à l'état de l'éditeur
  const useEditorState = () => {
    return {
      isEditorOpen,
      setIsEditorOpen,
      currentPoll,
      dispatchPoll,
    };
  };

  // Charger le sondage depuis localStorage au démarrage (CONDITIONNEL)
  // 🔧 Écouter les changements d'URL via location.search
  useEffect(() => {
    try {
      // 🔧 FIX: Si on vient de nettoyer, ne pas restaurer
      if (justClearedRef.current) {
        console.log(
          "🚫 [EditorStateProvider] Restauration bloquée - nettoyage récent",
        );
        justClearedRef.current = false;
        return;
      }

      const urlParams = new URLSearchParams(location.search);
      const conversationId = urlParams.get("conversationId");
      const resumeId = urlParams.get("resume");
      const isNewChat = urlParams.get("new");

      logger.debug("EditorStateProvider - URL params", "poll", {
        conversationId,
        resumeId,
        isNewChat,
      });

      // ✅ Ne restaurer QUE si on reprend une conversation existante
      const shouldRestore = conversationId || resumeId;

      if (shouldRestore) {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const poll = JSON.parse(stored);
          dispatchPoll({ type: "REPLACE_POLL", payload: poll });
          setIsEditorOpen(true);
          logger.info("✅ Sondage restauré depuis localStorage", "poll", {
            pollId: poll.id,
          });
        }
      } else {
        // 🔥 NOUVEAU: Extraction des données du "Magic Link" pour le prototype Crews
        const magicTitle = urlParams.get("title");
        const magicDatesStr = urlParams.get("dates");
        const pathname = location.pathname;

        if (magicTitle || magicDatesStr) {
          logger.info("🪄 Détection d'un Magic Link", "poll", {
            magicTitle,
            magicDatesStr,
          });

          const uid = () => Math.random().toString(36).slice(2, 10);
          const type = pathname.includes("/workspace/form") ? "form" : "date";

          const prefilledPoll: Partial<Poll> = {
            id: uid(),
            type,
            title:
              magicTitle ||
              (type === "date"
                ? t("editor.defaultTitle.date")
                : t("editor.defaultTitle.form")),
            status: "draft",
            settings: {
              showTimeSlots: type === "date",
              allowAnonymousResponses: true,
              ...(type === "date" && {
                allowAnonymousVotes: true,
                allowMaybeVotes: true,
                timeGranularity: 30,
                selectedDates: magicDatesStr ? magicDatesStr.split(",") : [],
              }),
            },
            ...(type === "date" &&
              magicDatesStr && {
                dates: magicDatesStr.split(","),
                selectedDates: magicDatesStr.split(","),
              }),
          };

          // Ne pas nettoyer le poll, mais l'écraser avec le pre-filled
          dispatchPoll({
            type: "REPLACE_POLL",
            payload: prefilledPoll as Poll,
          });
          setIsEditorOpen(true);
          logger.info(
            "🚀 Ouverture automatique de l'éditeur via Magic Link",
            "poll",
            { title: prefilledPoll.title },
          );
        } else {
          // ✅ Si pas de conversation à restaurer (nouveau chat ou navigation vers /), nettoyer LE POLL mais PAS l'état isEditorOpen
          logger.debug(
            "Nettoyage du poll (pas de conversation à restaurer)",
            "poll",
          );
          localStorage.removeItem(STORAGE_KEY);
          dispatchPoll({ type: "REPLACE_POLL", payload: null });

          // 🔥 NOUVEAU: Si on arrive depuis la landing page (/workspace/date ou /workspace/form),
          // on ouvre automatiquement l'éditeur pour montrer le sondage immédiatement
          if (
            pathname.includes("/workspace/date") ||
            pathname.includes("/workspace/form") ||
            pathname.includes("/workspace/availability")
          ) {
            setIsEditorOpen(true);
            logger.info(
              "🚀 Ouverture automatique de l'éditeur depuis landing page",
              "poll",
              {
                pathname,
              },
            );
          }
          // 🔧 FIX: Ne PAS fermer l'éditeur - il peut être ouvert volontairement (nouveau chat)
          // setIsEditorOpen(false); ← Commenté pour garder l'éditeur ouvert si déjà ouvert
          logger.info(
            "🧹 Poll nettoyé - état vierge (éditeur conservé)",
            "poll",
          );
        }
      }
    } catch (error) {
      logError(
        ErrorFactory.storage(
          "Failed to load poll from localStorage",
          t("editor.error.loadPoll"),
        ),
        {
          component: "EditorStateProvider",
          operation: "loadPoll",
          metadata: { error },
        },
      );
    }
  }, [location.search, location.pathname]); // 🔧 Se déclencher quand l'URL change

  // Sauvegarder le sondage dans localStorage ET pollStorage à chaque changement
  useEffect(() => {
    try {
      if (currentPoll) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(currentPoll));

        // 🔧 FIX: Normaliser le poll pour les sondages créés par l'IA
        const normalizedPoll = { ...currentPoll };

        // Si le sondage a `dates` ou `selectedDates` au niveau racine mais pas `settings.selectedDates`
        if (
          currentPoll.type === "date" &&
          (currentPoll.dates || currentPoll.selectedDates) &&
          !currentPoll.settings?.selectedDates
        ) {
          normalizedPoll.settings = {
            ...currentPoll.settings,
            selectedDates: currentPoll.selectedDates || currentPoll.dates,
          };
        }

        // 🔧 FIX: Sauvegarder aussi dans pollStorage pour que les modifications soient visibles
        // Mémoriser l'id serveur retourné → réutilisé par createPollFromChat pour éviter
        // un second roundtrip lors du clic "Créer ce sondage".
        addPoll(normalizedPoll).then((persistedId) => {
          if (persistedId) persistedPollIdRef.current = persistedId;
        });
      } else {
        localStorage.removeItem(STORAGE_KEY);
        // Plus de sondage courant (nouveau chat, CLEAR_POLL, Recommencer) :
        // oublier l'id persisté pour que la prochaine création parte de zéro
        // au lieu d'updater l'ancien sondage.
        persistedPollIdRef.current = null;
      }
    } catch (error) {
      logError(
        ErrorFactory.storage(
          "Failed to save poll to localStorage",
          t("editor.error.savePoll"),
        ),
        {
          component: "EditorStateProvider",
          operation: "savePoll",
          metadata: { error },
        },
      );
    }
  }, [currentPoll]);

  // Actions éditeur
  const openEditor = useCallback(() => {
    console.log("🔓 [EditorStateProvider] openEditor appelé");
    setIsEditorOpen(true);
    console.log("✅ [EditorStateProvider] Éditeur ouvert");
  }, []);

  const closeEditor = useCallback(() => {
    setIsEditorOpen(false);
  }, []);

  const toggleEditor = useCallback(() => {
    setIsEditorOpen((prev) => !prev);
  }, []);

  // Actions sondage
  const dispatchPollAction = useCallback((action: PollAction) => {
    dispatchPoll(action);
  }, []);

  const setCurrentPoll = useCallback((poll: Poll | null) => {
    dispatchPoll({ type: "REPLACE_POLL", payload: poll });
  }, []);

  const clearCurrentPoll = useCallback(() => {
    console.log("🧹 [EditorStateProvider] clearCurrentPoll appelé");
    justClearedRef.current = true; // 🔧 FIX: Marquer qu'on vient de nettoyer
    persistedPollIdRef.current = null;
    setIsEditorOpen(false); // 🔧 FIX: Fermer l'éditeur
    dispatchPoll({ type: "REPLACE_POLL", payload: null });
    localStorage.removeItem(STORAGE_KEY);
    console.log("✅ [EditorStateProvider] Poll et éditeur nettoyés");
  }, []);

  // Écouter l'événement chat-reset pour réinitialiser le formulaire
  useEffect(() => {
    const handleChatReset = (event: CustomEvent) => {
      if (event.detail?.resetType === "full") {
        console.log(
          "🧹 [EditorStateProvider] Réinitialisation complète du poll",
        );
        clearCurrentPoll();
      }
    };

    window.addEventListener("chat-reset", handleChatReset as EventListener);
    return () => {
      window.removeEventListener(
        "chat-reset",
        handleChatReset as EventListener,
      );
    };
  }, [clearCurrentPoll]);

  // Action combinée : créer un sondage depuis les données Gemini
  // Si un poll existe déjà, fusionner les questions au lieu de remplacer
  const createPollFromChat = useCallback(
    async (pollData: Partial<Poll> | Poll | PollSuggestion) => {
      logger.debug("createPollFromChat appelé", "poll", {
        pollData,
        currentPoll,
      });

      // Id du sondage déjà créé dans cette session (clic précédent sur
      // "Créer ce sondage", ou auto-persist). S'il est présent, le clic
      // suivant doit METTRE À JOUR ce sondage (côté droit) au lieu d'en
      // recréer un nouveau en DB. Sans ça, comme le slug porte un suffixe
      // aléatoire, aucune contrainte d'unicité ne bloque l'INSERT et on
      // empile des doublons (cf. bug hunt 2026-05-28 : 7 doublons en prod).
      const persistedId = persistedPollIdRef.current;

      // NB : on NE fusionne PAS les questions ici. La proposition IA (pollData)
      // contient toujours le formulaire complet voulu après modification ; un
      // append dupliquait les questions. De plus l'ancien bloc de fusion ne
      // faisait que muter currentPoll en local et `return` sans persister en DB
      // → « Mettre à jour » n'enregistrait rien (bug 2026-06-17). On laisse
      // donc passer vers create/update qui remplace les questions
      // (mergeUpdatesIntoPollData : questions présentes → remplacées).

      const now = new Date().toISOString();
      const slug = `poll-${Date.now()}`;
      const uid = () => Math.random().toString(36).slice(2, 10);

      // Convertir timeSlots si présents (vient de DatePollSuggestion)
      // ou utiliser timeSlotsByDate directement si c'est un Poll déjà converti.
      let timeSlotsByDate: Record<
        string,
        Array<{
          hour: number;
          minute: number;
          duration: number;
          enabled: boolean;
        }>
      > = {};
      if (
        "timeSlotsByDate" in pollData &&
        (pollData as any).timeSlotsByDate &&
        Object.keys((pollData as any).timeSlotsByDate).length > 0
      ) {
        // Poll déjà converti (après modifications) — utiliser directement.
        timeSlotsByDate = (pollData as any).timeSlotsByDate;
      } else if (
        "timeSlots" in pollData &&
        pollData.timeSlots &&
        Array.isArray(pollData.timeSlots) &&
        pollData.timeSlots.length > 0
      ) {
        // Import dynamique pour éviter les dépendances circulaires
        const { convertGeminiSlotsToTimeSlotsByDate } =
          await import("../../services/TimeSlotConverter");

        const convertedSlots = convertGeminiSlotsToTimeSlotsByDate(
          pollData.timeSlots as any[], // Cast pour éviter les erreurs de type
          "dates" in pollData && pollData.dates && Array.isArray(pollData.dates)
            ? pollData.dates
            : [],
          30, // Granularité par défaut 30min
        );

        // Convertir InternalTimeSlot[] vers le format attendu
        timeSlotsByDate = Object.fromEntries(
          Object.entries(convertedSlots).map(([date, slots]) => [
            date,
            slots.map((slot) => ({
              hour: slot.hour,
              minute: slot.minute,
              duration: slot.duration || 30, // Assurer que duration est présent
              enabled: slot.enabled,
            })),
          ]),
        );
      }

      // Convertir les questions Gemini en format FormPollCreator
      // pollData.questions peut être FormQuestion[] (de PollSuggestion) ou FormQuestionShape[] (de Poll)
      const pollQuestions =
        "questions" in pollData && pollData.questions ? pollData.questions : [];
      let convertedQuestions: import("../../lib/pollStorage").FormQuestionShape[] =
        [];
      if (
        pollData.type === "form" &&
        "questions" in pollData &&
        pollData.questions
      ) {
        logger.debug("Conversion questions Gemini", "poll", {
          questions: pollData.questions,
        });
        // Type assertion: si c'est un PollSuggestion, questions est FormQuestion[]
        const geminiQuestions =
          pollQuestions as import("../../lib/ai/products/form/FormPollService").FormQuestion[];
        convertedQuestions = geminiQuestions.map(
          (
            q: import("../../lib/ai/products/form/FormPollService").FormQuestion,
          ) => {
            // Use the mapToStorageQuestion helper function for consistency
            return mapToStorageQuestion(q as any, uid);
          },
        );
        logger.debug("Questions converties", "poll", { convertedQuestions });
      }

      // ✅ Utiliser les hooks centralisés pour sauvegarder dans Supabase
      try {
        let pollResult;

        if (pollData.type === "form") {
          // Créer un formulaire via createPoll (useFormPollCreation migré vers form-polls)
          const formPollData: import("../../hooks/usePolls").FormPollData = {
            type: "form",
            title: pollData.title || t("editor.defaultTitle.form"),
            description: undefined,
            questions: convertedQuestions.map(
              (q: import("../../lib/pollStorage").FormQuestionShape) => ({
                id: q.id,
                kind: q.type,
                title: q.title,
                required: q.required || false,
                options: q.options,
                maxChoices: q.maxChoices,
                placeholder: q.placeholder,
                maxLength: q.maxLength,
                type: q.type,
                // Date-specific fields
                selectedDates: (q as any).selectedDates,
                timeSlotsByDate: (q as any).timeSlotsByDate,
                timeGranularity: (q as any).timeGranularity,
                allowMaybeVotes: (q as any).allowMaybeVotes,
                allowAnonymousVotes: (q as any).allowAnonymousVotes,
                // Rating-specific fields
                ratingScale: q.ratingScale,
                ratingStyle: q.ratingStyle,
                ratingMinLabel: q.ratingMinLabel,
                ratingMaxLabel: q.ratingMaxLabel,
                // Matrix-specific fields
                matrixRows: q.matrixRows,
                matrixColumns: q.matrixColumns,
                matrixType: q.matrixType,
                matrixColumnsNumeric: q.matrixColumnsNumeric,
                // Text validation fields
                validationType: q.validationType,
              }),
            ),
            settings: {
              allowAnonymousResponses: true,
              expiresAt: undefined,
            },
          };
          pollResult = persistedId
            ? await updatePoll(persistedId, formPollData as any)
            : await createPoll(formPollData);
        } else {
          // Créer un sondage de dates
          logger.info("💾 Création sondage via IA", "poll", {
            title: pollData.title,
          });
          // Résoudre les dates : PollSuggestion → .dates, Poll → .selectedDates
          const resolvedDates =
            ("dates" in pollData && pollData.dates
              ? (pollData as any).dates
              : "selectedDates" in pollData
                ? (pollData as any).selectedDates
                : []) || [];

          const datePollData: import("../../hooks/usePolls").DatePollData = {
            type: "date",
            title: pollData.title || t("editor.defaultTitle.poll"),
            description: undefined,
            selectedDates: resolvedDates,
            timeSlotsByDate: timeSlotsByDate,
            participantEmails: [],
            dateGroups: ("dateGroups" in pollData
              ? pollData.dateGroups
              : undefined) as
              | Array<{
                  dates: string[];
                  label: string;
                  type: "weekend" | "week" | "fortnight" | "custom";
                }>
              | undefined, // 🔧 Préserver les groupes de dates
            settings: {
              selectedDates: resolvedDates,
              timeGranularity: 30,
              allowAnonymousVotes: true,
              allowMaybeVotes: true,
              sendNotifications: false,
              expiresAt: undefined,
            },
          };
          pollResult = persistedId
            ? await updatePoll(persistedId, datePollData as any)
            : await createPoll(datePollData);
        }

        if (pollResult.error || !pollResult.poll) {
          logger.error("❌ Erreur création poll via IA", "poll", {
            error: pollResult.error,
          });
          throw ErrorFactory.storage(
            pollResult.error || "Impossible de créer le poll",
            t("editor.error.createPoll"),
          );
        }

        // Utiliser le poll créé
        const poll = pollResult.poll;
        persistedPollIdRef.current = poll.id;
        setCurrentPoll(poll);
        setIsEditorOpen(true);

        logger.info("✅ Poll créé via IA et sauvegardé dans Supabase", "poll", {
          pollId: poll.id,
          pollType: poll.type,
          conversationId: poll.conversationId,
        });

        // 🔧 FIX: Sauvegarder le pollId dans la conversation pour affichage dashboard
        const conversationId = new URLSearchParams(window.location.search).get(
          "conversationId",
        );
        if (conversationId) {
          try {
            const { getConversation, updateConversation } =
              await import("../../lib/storage/ConversationStorageSimple");
            const conversation = getConversation(conversationId);
            if (conversation) {
              updateConversation({
                ...conversation,
                pollId: poll.id, // ✅ Utiliser 'pollId' (pas 'relatedPollId')
                pollType: poll.type as "date" | "form",
                pollStatus: poll.status,
                updatedAt: new Date(),
              });
              logger.info("✅ Poll lié à la conversation", "poll", {
                conversationId,
                pollId: poll.id,
                pollType: poll.type,
              });
            }
          } catch (error) {
            logError(
              ErrorFactory.storage(
                "Impossible de mettre à jour la conversation avec pollId",
                t("editor.error.saveConversation"),
              ),
              { metadata: { error } },
            );
          }
        }
      } catch (error) {
        logger.error("Erreur lors de la sauvegarde", error);
        // Rethrow : sans ça, onPollCreated résout malgré l'échec → le bouton
        // bascule à tort sur "Mettre à jour" (pollAlreadyCreated=true) et
        // l'utilisateur croit le sondage créé alors qu'aucune question n'est
        // persistée (cf. 500 create-poll, 2026-06-17).
        throw error;
      }
    },
    [currentPoll, setCurrentPoll, createPoll, updatePoll, t],
  );

  const value: EditorStateContextType = {
    isEditorOpen,
    currentPoll,
    openEditor,
    closeEditor,
    toggleEditor,
    dispatchPollAction,
    setCurrentPoll,
    clearCurrentPoll,
    createPollFromChat,
  };

  return (
    <EditorStateContext.Provider value={value}>
      {children}
    </EditorStateContext.Provider>
  );
}

/**
 * Hook pour accéder à l'état éditeur
 *
 * @throws Error si utilisé hors du EditorStateProvider
 */
export function useEditorState(): EditorStateContextType {
  const context = useContext(EditorStateContext);

  // Ajouter un guard pour éviter l'erreur pendant les renders
  if (!context) {
    console.warn(
      "🔍 useEditorState called outside EditorStateProvider - returning default context",
    );
    // Retourner un contexte par défaut pour éviter l'erreur
    return {
      isEditorOpen: false,
      currentPoll: null,
      openEditor: () => {},
      closeEditor: () => {},
      toggleEditor: () => {},
      dispatchPollAction: () => {},
      setCurrentPoll: () => {},
      clearCurrentPoll: () => {},
      createPollFromChat: async () => {},
    };
  }

  return context;
}

/**
 * Hooks spécialisés pour éviter re-renders inutiles
 */

/**
 * Hook pour accéder uniquement au sondage actuel
 * Le composant ne re-render que si currentPoll change
 */
export function useCurrentPoll() {
  const { currentPoll } = useEditorState();
  return currentPoll;
}

/**
 * Hook pour accéder uniquement à l'état d'ouverture de l'éditeur
 * Le composant ne re-render que si isEditorOpen change
 */
export function useIsEditorOpen() {
  const { isEditorOpen } = useEditorState();
  return isEditorOpen;
}

/**
 * Hook pour accéder uniquement aux actions de l'éditeur
 * Le composant ne re-render jamais (actions stables)
 */
export function useEditorActions() {
  const {
    openEditor,
    closeEditor,
    toggleEditor,
    dispatchPollAction,
    setCurrentPoll,
    clearCurrentPoll,
    createPollFromChat,
  } = useEditorState();
  return {
    openEditor,
    closeEditor,
    toggleEditor,
    dispatchPollAction,
    setCurrentPoll,
    clearCurrentPoll,
    createPollFromChat,
  };
}
